<template>
  <div>
    <mcw-button @click="openDialog" >
        <span v-if="messages.length > 0">{{ messages.length }}</span>
        <mcw-material-icon icon="chat" class="chat-button" />
    </mcw-button>

    <transition name="fade">
      <div
        v-if="isOpen"
        class="chat-modal-overlay"
        @click.self="closeDialog"
      >
        <transition name="slide-up">
          <div class="chat-modal" v-if="isOpen">
            <div class="chat-header">
              <div class="chat-header-content">
                <h3 class="chat-title">{{ title }}</h3>
                <p class="chat-subtitle">{{ subtitle }}</p>
              </div>
              <button @click="closeDialog" class="chat-close-btn">
                &times;
              </button>
            </div>

            <div class="chat-messages" ref="messagesContainer">
              <div
                v-for="(message, index) in messages"
                :key="index"
                :class="['message', message.login == login ? 'message-own' : 'message-other']"
              >
                <div class="message-content">
                  <div class="message-text">{{ message.text }}</div>
                  <div class="message-meta">
                    <span class="message-date">{{ formatDate(message.date) }}</span>
                    <span class="message-login">ID: {{ message.login }}</span>
                  </div>
                </div>
              </div>

              <div v-if="messages.length === 0" class="no-messages">
                Нет сообщений. Будьте первым!
              </div>
            </div>

            <div class="chat-input-area">
              <textarea
                v-model="newMessage"
                @keydown.enter.exact.prevent="sendMessage"
                placeholder="Введите сообщение..."
                class="chat-input"
                rows="3"
              ></textarea>
              <div class="chat-actions">
                <button @click="sendMessage" class="btn-send" :disabled="!newMessage.trim()">
                  Отправить
                </button>
                <button @click="closeDialog" class="btn-cancel">
                  Закрыть
                </button>
              </div>
            </div>
          </div>
        </transition>
      </div>
    </transition>
  </div>
</template>

<script>
import { mapState } from 'vuex';

export default {
  name: 'ChatDialog',
  props: {
    title: {
      type: String,
      default: 'Диалог',
    },
    subtitle: {
      type: String,
      default: '',
    },
    messages: {
      type: Array,
      default: () => [],
    },
    id: {
      type: Number,
      required: true,
    },
  },
  data() {
    return {
      isOpen: false,
      newMessage: '',
    };
  },
  watch: {
    messages: {
      handler() {
        this.$nextTick(() => {
          this.scrollToBottom();
        });
      },
      deep: true,
    },
    isOpen(newVal) {
      if (newVal) {
        document.body.style.overflow = 'hidden';
        this.$nextTick(() => {
          this.scrollToBottom();
        });
      } else {
        document.body.style.overflow = '';
      }
    },
  },
  methods: {
    openDialog() {
      this.isOpen = true;
    },
    closeDialog() {
      this.isOpen = false;
      this.newMessage = '';
    },
    sendMessage() {
      if (!this.newMessage.trim()) return;

      const message = {
        text: this.newMessage.trim(),
        date: new Date().toISOString(),
        login: this.login,
      };

      this.$emit('send-message', message, this.id);
      this.newMessage = '';

      this.$nextTick(() => {
        this.scrollToBottom();
      });
    },
    scrollToBottom() {
      const container = this.$refs.messagesContainer;
      if (container) {
        container.scrollTop = container.scrollHeight;
      }
    },
    formatDate(dateString) {
      const date = new Date(dateString);
      return date.toLocaleTimeString([], {
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
      });
    },
    handleEscape(e) {
      if (e.key === 'Escape' && this.isOpen) {
        this.closeDialog();
      }
    },
  },
  mounted() {
    document.addEventListener('keydown', this.handleEscape);
  },
  computed: {
    ...mapState({
      login: (state) => state.user.user.id_login,
    }),
  },
  beforeUnmount() {
    document.removeEventListener('keydown', this.handleEscape);
    document.body.style.overflow = '';
  },
};
</script>

<style scoped>
.chat-button {
  color: #337ab7;
}

/* Фон модального окна */
.chat-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
}

.chat-modal {
  background: white;
  border-radius: 12px;
  width: 100%;
  max-width: 500px;
  max-height: 80vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.2);
  overflow: hidden;
}

.fade-enter-active, .fade-leave-active {
  transition: opacity 0.3s;
}

.fade-enter, .fade-leave-to {
  opacity: 0;
}

.slide-up-enter-active {
  animation: slideUp 0.4s cubic-bezier(0.16, 1, 0.3, 1);
}

.slide-up-leave-active {
  animation: slideUp 0.3s cubic-bezier(0.16, 1, 0.3, 1) reverse;
}

@keyframes slideUp {
  from {
    opacity: 0;
    transform: translateY(50px) scale(0.95);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

.chat-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 20px;
  border-bottom: 1px solid #eaeaea;
  background: #f8f9fa;
}

.chat-header-content {
  flex: 1;
}

.chat-title {
  margin: 0 0 5px 0;
  font-size: 1.4rem;
  font-weight: 600;
  color: #333;
}

.chat-subtitle {
  margin: 0;
  font-size: 0.9rem;
  color: #666;
}

.chat-close-btn {
  background: none;
  border: none;
  font-size: 28px;
  color: #999;
  cursor: pointer;
  padding: 0;
  width: 30px;
  height: 30px;
  line-height: 30px;
  transition: color 0.2s;
}

.chat-close-btn:hover {
  color: #333;
}

.chat-messages {
  flex: 1;
  overflow-y: auto;
  padding: 20px;
  min-height: 300px;
  max-height: 50vh;
}

.message {
  margin-bottom: 16px;
  display: flex;
}

.message-own {
  justify-content: flex-end;
}

.message-other {
  justify-content: flex-start;
}

.message-content {
  max-width: 70%;
  padding: 12px 16px;
  border-radius: 18px;
  position: relative;
}

.message-own .message-content {
  background: #4a6cf7;
  color: white;
  border-bottom-right-radius: 4px;
}

.message-other .message-content {
  background: #f0f2f5;
  color: #333;
  border-bottom-left-radius: 4px;
}

.message-text {
  word-wrap: break-word;
  line-height: 1.4;
}

.message-meta {
  font-size: 0.75rem;
  margin-top: 6px;
  opacity: 0.8;
  display: flex;
  justify-content: space-between;
}

.no-messages {
  text-align: center;
  color: #999;
  padding: 40px 20px;
  font-style: italic;
}

.chat-input-area {
  border-top: 1px solid #eaeaea;
  padding: 20px;
  background: #f8f9fa;
}

.chat-input {
  width: 100%;
  padding: 12px 16px;
  border: 2px solid #ddd;
  border-radius: 8px;
  font-family: inherit;
  font-size: 14px;
  resize: none;
  margin-bottom: 15px;
  transition: border-color 0.3s;
  box-sizing: border-box;
}

.chat-input:focus {
  outline: none;
  border-color: #4a6cf7;
}

.chat-actions {
  display: flex;
  gap: 10px;
  justify-content: flex-end;
}

.btn-send, .btn-cancel {
  padding: 10px 24px;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  border: none;
  transition: all 0.3s;
}

.btn-send {
  background: #4a6cf7;
  color: white;
}

.btn-send:hover:not(:disabled) {
  background: #3a5ce5;
  transform: translateY(-1px);
}

.btn-send:disabled {
  background: #ccc;
  cursor: not-allowed;
}

.btn-cancel {
  background: #6c757d;
  color: white;
}

.btn-cancel:hover {
  background: #5a6268;
  transform: translateY(-1px);
}

.chat-messages::-webkit-scrollbar {
  width: 6px;
}

.chat-messages::-webkit-scrollbar-track {
  background: #f1f1f1;
}

.chat-messages::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 3px;
}

.chat-messages::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}
</style>
